import { Box, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui-compat"
import { FilterDropdown } from "components/FilterDropdown"
import SearchVideosBar from "components/videos/SearchVideosBar"
import { VideosChipsFilter } from "components/videos/VideosChipsFilter"
import { useVideosSearch } from "hooks/videos/useVideosSearch"
import ConventionTag from "models/convention-api/v1/ConventionTag"
import React, { useMemo, useState } from "react"
import { Filter } from "types/types"

interface VideosHeaderProps {
  videosSearch: ReturnType<typeof useVideosSearch>
  tags: ConventionTag[]
}

const useStyles = makeStyles()(
  (theme) => ({
    headerContainer: {
      display: "grid",
      gridTemplateColumns: "auto",
      gridGap: 40,

      paddingTop: 20,
      paddingBottom: 20,

      [theme.breakpoints.up(720)]: {
        gridTemplateColumns: `auto`,
        paddingTop: 30,
        paddingBottom: 30,
      },
    },
    pageTitleTypography: {
      ...theme.fonts.raleway500,
      fontSize: 36,
      lineHeight: 1.17,
    },
    searchContainer: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gridGap: 25,

      [theme.breakpoints.up(400)]: {
        gridTemplateColumns: "1fr 1fr",
      },
      [theme.breakpoints.up(720)]: {
        justifyContent: "space-between",
        gridTemplateColumns: "auto auto",
      },
    },
    filterDropdownContainer: {
      display: "flex",
      justifyContent: "flex-end",
    },
  })
)

export const VideosHeader = (props: VideosHeaderProps) => {
  const { videosSearch, tags } = props
  const { classes } = useStyles()
  const [searchValue, setSearchValue] = useState("")

  const onSearchValueChange = (newValue: string) => {
    setSearchValue(newValue)
  }

  const performSearchingBySearchValue = () => {
    if (searchValue.trim().length === 0 && videosSearch.query.trim().length === 0) return

    videosSearch.setQuery(searchValue)
    videosSearch.setTagIds([])
  }
  const clearSearchValue = () => {
    setSearchValue("")
    videosSearch.setQuery("")
  }

  const onChipsFilterToggle = (tagId: string, isChecked: boolean) => {
    videosSearch.setTagIds((prevTagIds) =>
      isChecked ? prevTagIds.filter((prevTagId) => prevTagId !== tagId) : [...prevTagIds, tagId],
    )
  }

  const onApplyFilterDropdownList = (newTagIds: string[]) => {
    videosSearch.setTagIds(newTagIds)
  }

  const filterDropdownList: Filter[] = useMemo(() => {
    return tags.map((tag) => ({ value: tag.id, label: tag.name }))
  }, [tags])

  // we show filter chips when there are less than 5 tags and filter dropdown when there are more than 5 tags
  const hasMore5tags = tags.length > 5
  return (
    <Box className={classes.headerContainer}>
      <Typography className={classes.pageTitleTypography} component="h1">
        Explore Videos
      </Typography>
      <Box className={classes.searchContainer}>
        <SearchVideosBar
          onChange={onSearchValueChange}
          value={searchValue}
          onSearch={performSearchingBySearchValue}
          onEnter={performSearchingBySearchValue}
          onClear={clearSearchValue}
          iconType={videosSearch.query.length === 0 ? "search" : "clear"}
        />
        {hasMore5tags && (
          <Box className={classes.filterDropdownContainer}>
            <FilterDropdown
              id="videos-filter-dropdown"
              list={filterDropdownList}
              defaultSelectedList={videosSearch.tagIds}
              onApply={onApplyFilterDropdownList}
            />
          </Box>
        )}
      </Box>
      {!hasMore5tags && (
        <VideosChipsFilter tags={tags} videosSearch={videosSearch} onToggle={onChipsFilterToggle} />
      )}
    </Box>
  )
}
